
import { HttpClient } from "@angular/common/http";
import { Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { environment } from 'environments/environment';
import { from, Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { Company } from "./company-raw/company-raw.types";
import { CompanyDT } from "./company-with-country-and-sector/company-dt.types";
import { utf8ToEnglishNormalize } from "./utf8-map";

// return true if company includes query string
export function testCompanyDTUsingString(company: CompanyDT, query: string, processedQuery = false) {
    if (!company) return false
    const filterValue = processedQuery ? query : utf8ToEnglishNormalize(query.trim().toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
    //console.log(company.country)
    const result =
        utf8ToEnglishNormalize(company.name.toLowerCase().replace(/ /g, ''))?.includes(filterValue)
        || utf8ToEnglishNormalize(company.country?.countryName?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue)
        || utf8ToEnglishNormalize(company.country?.countryCode?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue)
        || (utf8ToEnglishNormalize(company.sector?.industryCode?.toLowerCase().replace(/ /g, ''))?.includes(filterValue))
        || (utf8ToEnglishNormalize(company.sector?.industryName?.toLowerCase().replace(/ /g, ''))?.includes(filterValue))
        || (utf8ToEnglishNormalize(company.sector?.sectorCode?.toLowerCase().replace(/ /g, ''))?.includes(filterValue))
        || (utf8ToEnglishNormalize(company.sector?.sectorName?.toLowerCase().replace(/ /g, ''))?.includes(filterValue))
        || company.tags.find(t => utf8ToEnglishNormalize(t?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue))
        || (company.companyNameInReports || []).find(t => utf8ToEnglishNormalize(t?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue));
    return result
}

// return true if company elements (sector, country ... ) includes query string (does not apply on company name)
export function testCompanyDTInformationsUsingString(company: CompanyDT, query: string, processedQuery = false) {
    if (!company) return false
    const filterValue = processedQuery ? query : utf8ToEnglishNormalize(query.trim().toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
    const result = utf8ToEnglishNormalize(company.country?.countryName?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue)
        || utf8ToEnglishNormalize(company.country?.countryCode?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue)
        || (utf8ToEnglishNormalize(company.sector?.industryCode?.toLowerCase().replace(/ /g, ''))?.includes(filterValue))
        || (utf8ToEnglishNormalize(company.sector?.industryName?.toLowerCase().replace(/ /g, ''))?.includes(filterValue))
        || (utf8ToEnglishNormalize(company.sector?.sectorCode?.toLowerCase().replace(/ /g, ''))?.includes(filterValue))
        || (utf8ToEnglishNormalize(company.sector?.sectorName?.toLowerCase().replace(/ /g, ''))?.includes(filterValue))
        || company.tags.find(t => utf8ToEnglishNormalize(t?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue))
    return result
}
// return true if the company name starts with the query string
export function testCompanyNameStartingWithString(company: CompanyDT, query: string, processedQuery = false) {
    if (!company) return false
    const filterValue = processedQuery ? query : utf8ToEnglishNormalize(query.trim().toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
    return utf8ToEnglishNormalize(company.name.toLowerCase().replace(/ /g, ''))?.startsWith(filterValue) || (company.companyNameInReports || []).find(t => utf8ToEnglishNormalize(t?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.startsWith(filterValue));
}

// return true if company name includes query string
export function testCompanyNameIncludesString(company: CompanyDT, query: string, processedQuery = false) {
    if (!company) return false
    const filterValue = processedQuery ? query : utf8ToEnglishNormalize(query.trim().toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
    return utf8ToEnglishNormalize(company.name.toLowerCase().replace(/ /g, ''))?.includes(filterValue) || (company.companyNameInReports || []).find(t => utf8ToEnglishNormalize(t?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue));
}

export function companyDTtitle(c: CompanyDT): string {
    if (!c) return ""
    return [c.name].concat(c.companyNameInReports).concat(c.sector ? [c.sector.sectorName.toLowerCase()] : []).concat(c.country ? [c.country.countryCode] : []).concat(c.tags).join(' | ')
}

export function testCompanyUsingString(company: Company, query: string, processedQuery = false) {
    if (!company) return false
    const filterValue = processedQuery ? query : utf8ToEnglishNormalize(query.trim().toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
    const result =
        utf8ToEnglishNormalize(company.name.toLowerCase().replace(/ /g, ''))?.includes(filterValue)
        || utf8ToEnglishNormalize(company?.country?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue)
        || utf8ToEnglishNormalize(company.sector?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue)
        || company.tags.find(t => utf8ToEnglishNormalize(t?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue))
        || (company.companyNameInReports || []).find(t => utf8ToEnglishNormalize(t?.toLowerCase().replace(/ /g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, ''))?.includes(filterValue));
    return result
}

export function companyTitle(c: Company): string {
    if (!c) return ""
    return [c.name].concat(c.companyNameInReports).concat(c.sector ? [c.sector.toLowerCase()] : []).concat(c.country ? [c.country.toLowerCase()] : []).concat(c.tags).join(' | ')
}


export function getLogo(n: number): string {
    return environment.server + 'api/issuer/' + (n ?? 0) + "/logo"
}

// sort companies without taking into consideration the ( " ) existing as prefix in some companies's name
export function sortCompaniesAlphabetically<T>(companiesArray: T[], getString: (item: T) => string): T[] {
    return companiesArray.sort((a, b) => {
        return compareStringsWhileIgnoringStartingQuote(getString(a), getString(b))
    });
}

export function compareStringsWhileIgnoringStartingQuote(a: string, b: string): number {
    let aStr = a.startsWith("\"") ? a.substring(1) : a;
    let bStr = b.startsWith("\"") ? b.substring(1) : b;

    return aStr.localeCompare(bStr);
}

@Pipe({
    name: 'protectedLink',
})
export class ProtectedLinkPipe implements PipeTransform {
    constructor(private httpClient: HttpClient, private _sanitizer: DomSanitizer) {

    }


    transform(url: string): Observable<string> {
        return this.httpClient.get(url, { responseType: 'blob' }).pipe(
            switchMap((blob: Blob) => {
                return from(new Promise((resolve, _) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                }))
            }),
            map((b: string) => {
                //console.log(b)
                return this._sanitizer.bypassSecurityTrustResourceUrl(b)
            }),
            catchError((er) => {
                return of();
            })) as Observable<string>
    }
}

